import { z } from 'zod';
/**
 * The overview.
 *
 * One request that answers "is anything wrong right now" — counts across the
 * product's main tables, the dependency probes the readiness endpoint already
 * runs, and a daily token series for the chart.
 *
 * Design spec §12 flags these aggregates as the one place performance was
 * deferred. `queryMs` is the answer to that: the screen reports what it cost to
 * build, so a slow overview is a number an operator can see rather than a
 * feeling, and any index added later has a before to compare against.
 */
export const OVERVIEW_WINDOW_DAYS = 30;
export const OVERVIEW_MAX_WINDOW_DAYS = 90;
export const adminOverviewQuerySchema = z.object({
    days: z.coerce.number().int().min(7).max(OVERVIEW_MAX_WINDOW_DAYS).default(OVERVIEW_WINDOW_DAYS),
});
export const DEPENDENCY_STATUSES = ['up', 'down'];
export const adminDependencySchema = z.object({
    name: z.string(),
    status: z.enum(DEPENDENCY_STATUSES),
    /** Null when the probe failed or timed out. */
    latencyMs: z.number().int().nullable(),
});
export const adminQueueHealthSchema = z.object({
    name: z.string(),
    isPaused: z.boolean(),
    failed: z.number().int(),
    waiting: z.number().int(),
});
export const adminOverviewHealthSchema = z.object({
    status: z.enum(['ok', 'degraded']),
    uptimeSeconds: z.number().int(),
    version: z.string(),
    dependencies: z.array(adminDependencySchema),
    /**
     * Empty when Redis is unreachable — the counts cannot be read at all then,
     * and inventing zeros would say "no failed jobs" when the truth is "no idea".
     */
    queues: z.array(adminQueueHealthSchema),
});
/** One day of model spend, for the chart. */
export const adminSpendPointSchema = z.object({
    /** Local date, `YYYY-MM-DD`. */
    date: z.string(),
    inputTokens: z.number().int(),
    outputTokens: z.number().int(),
    /** Null when nothing that day reported a cost. */
    costUsd: z.number().nullable(),
});
export const adminOverviewSchema = z.object({
    generatedAt: z.string(),
    windowDays: z.number().int(),
    /**
     * Milliseconds the aggregate queries took. Design spec §12 deferred
     * optimising them; this is how anyone would know they need it.
     */
    queryMs: z.number().int(),
    users: z.object({
        total: z.number().int(),
        /** Not soft-deleted. */
        active: z.number().int(),
        deactivated: z.number().int(),
        onboarded: z.number().int(),
        newInWindow: z.number().int(),
        /**
         * Active accounts by the platform of their most recently seen device.
         * `none` is an account that has never registered a device: signed up on
         * a build that predates the registration, or never got past sign-in.
         * iOS builds before 26.3.2 reported themselves as Android, so the iOS
         * number only becomes true as that build rolls out.
         */
        byPlatform: z.object({
            ios: z.number().int(),
            android: z.number().int(),
            web: z.number().int(),
            none: z.number().int(),
        }),
    }),
    plans: z.object({
        activeWeeks: z.number().int(),
        generating: z.number().int(),
        failedInWindow: z.number().int(),
        /**
         * Weeks committed with a numeric residual recorded against them, rather
         * than failed. The never-fail terminal turned that class of failure into a
         * plan somebody eats, which is the right trade and also the risk: a
         * failure nobody sees is a failure nobody fixes. A rising number here is
         * the catalog saying it cannot hit the targets we are setting.
         */
        approximatedInWindow: z.number().int(),
        /**
         * Runs whose day bounds the feasibility precheck had to widen before the
         * model was called at all — targets the user's own permitted food cannot
         * reach. A product question, not an incident.
         */
        relaxedInWindow: z.number().int(),
    }),
    billing: z.object({
        premium: z.number().int(),
        trialing: z.number().int(),
        billingIssue: z.number().int(),
    }),
    scans: z.object({
        inWindow: z.number().int(),
        failedInWindow: z.number().int(),
    }),
    health: adminOverviewHealthSchema,
    spend: z.object({
        /**
         * The denominator the screen needs to say what a call costs on average.
         * Without it the console can only show a total, which says nothing about
         * whether the bill moved because usage grew or because a model did.
         */
        calls: z.number().int(),
        costUsd: z.number().nullable(),
        /** True when at least one call in the window reported no cost. */
        costIsPartial: z.boolean(),
        inputTokens: z.number().int(),
        outputTokens: z.number().int(),
        /** One point per day in the window, oldest first, gaps filled with zeros. */
        series: z.array(adminSpendPointSchema),
    }),
});
// --- API console ------------------------------------------------------------
/**
 * The OpenAPI document, as the API it is pointed at generates it.
 *
 * Read from the running API rather than bundled into the console at build time:
 * a bundled copy would describe whatever was true when the console was built,
 * not what the API it talks to serves now.
 *
 * Loosely typed on purpose — this is a JSON Schema document and the console
 * reads a handful of fields from it. Restating OpenAPI 3.1 in Zod would be a
 * second, worse specification to keep in step.
 */
export const adminOpenApiOperationSchema = z
    .object({
    summary: z.string().optional(),
    description: z.string().optional(),
    tags: z.array(z.string()).optional(),
    security: z.array(z.record(z.unknown())).optional(),
    parameters: z
        .array(z
        .object({
        name: z.string(),
        in: z.string(),
        required: z.boolean().optional(),
        description: z.string().optional(),
    })
        .passthrough())
        .optional(),
    requestBody: z.record(z.unknown()).optional(),
})
    .passthrough();
export const adminOpenApiSchema = z
    .object({
    openapi: z.string(),
    info: z.object({ title: z.string(), version: z.string() }).passthrough(),
    paths: z.record(z.record(z.unknown())),
})
    .passthrough();
/** The methods the console will offer to send. */
export const API_CONSOLE_METHODS = ['get', 'post', 'put', 'patch', 'delete'];
//# sourceMappingURL=overview.js.map